// @ts-nocheck
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCell, DataTableRow, Typography } from "@insite/mobius/index";
import { TypographyProps } from "@insite/mobius/Typography";
import { linkStyles } from "@store/Themes/common/mixins/link";
import constants from "@store/constants";
import getAttributeTypeValue from "@store/Framework/Common/Utilities/getAttributeTypeValue";
import React from "react";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ProductPrice, { ProductPriceStyles } from "@store/Overrides/Components/ProductPrice";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { productCardProps } from "@store/Themes/common/props/productCard";
import ShoppingCartLine from "@store/Library/Components/Toolkit/Icons/ShoppingCart/ShoppingCartLine";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useGetRelatedProductInfoFromList } from "@store/Framework/Store/Store/Components/ProductInfoList/ProductInfoListHooks";
import AddToCartOptionsAndButton, {
    AddToCartOptionsAndButtonStyles,
} from "@store/Library/Components/Toolkit/AddToCartOptionsAndButton";

export interface ProducFastenersDataTableRowProps {
    product: ProductModel;
    tableId: string;
    showLength: boolean;
    showHeadDiameter: boolean;
    showThread: boolean;
    showPoint: boolean;
    showHead: boolean;
    showFinish: boolean;
}

export interface ProductFastenersDataTableRowStyles {
    itemCell?: DataTableCellProps;
    lengthCell?: DataTableCellProps;
    boxCell?: DataTableCellProps;
    headDiameterCell?: DataTableCellProps;
    threadCell?: DataTableCellProps;
    driveCell?: DataTableCellProps;
    pointCell?: DataTableCellProps;
    headCell?: DataTableCellProps;
    finishCell?: DataTableCellProps;
    actionsCell?: DataTableCellProps;
    itemNumber?: TypographyProps;
    itemNumberLink?: LinkPresentationProps;
    actionsWrapper?: InjectableCss;
    actionsInnerWrapper?: InjectableCss;
    addToCartOptionsAndButton?: AddToCartOptionsAndButtonStyles;
    addToListLinkStyles?: ProductAddToListLinkStyles;
    productPrice?: ProductPriceStyles;
}

export const productFastenersDataTableRowStyles: ProductFastenersDataTableRowStyles = {
    itemNumber: {
        css: css`
            color: ${getColor("primary")};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    itemNumberLink: {
        css: css`
            ${linkStyles("text.link", "text.linkHover", false, true)}
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            align-items: flex-start;
            gap: ${pxToRem(10)};
        `,
    },
    actionsInnerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: ${pxToRem(10)};
        `,
    },
    addToCartOptionsAndButton: {
        ...productCardProps.addToCartOptionsAndButton,
        flexWrapper: {
            extendedStyles: {
                content: {
                    css: css`
                        flex-wrap: nowrap;
                        gap: 0;
                    `,
                },
            },
        },
        addToCartButton: {
            showButtonIconOnly: true,
            extendedStyles: {
                button: {
                    icon: {
                        position: "right",
                        src: ShoppingCartLine,
                    },
                    css: css`
                        width: ${pxToRem(50)};
                        min-width: ${pxToRem(50)};
                    `,
                },
            },
        },
        quantityOrdered: {
            ...productCardProps.addToCartOptionsAndButton?.quantityOrdered,
            labelOverride: "",
        },
    },
    addToListLinkStyles: productCardProps.addToListLink,
    productPrice: {
        wrapper: {
            css: css`
                align-items: flex-end;
                min-width: ${pxToRem(50)};
            `,
        },
    },
};

const styles = productFastenersDataTableRowStyles;

const ProductFastenersDataTableRow = ({
    product,
    tableId,
    showLength,
    showHeadDiameter,
    showThread,
    showPoint,
    showHead,
    showFinish,
}: ProducFastenersDataTableRowProps) => {
    const dispatch = useAppDispatch();
    const productInfo = useGetRelatedProductInfoFromList(tableId, product.id);

    if (!productInfo) {
        return null;
    }

    const updateQuantity = (qtyOrdered: number) => {
        dispatch(updateProductInfo({ id: tableId, productId: product.id, qtyOrdered }));
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        dispatch(updateProductInfo({ id: tableId, productId: product.id, unitOfMeasure }));
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <DataTableRow key={product.id}>
                <DataTableCell {...styles.itemCell} data-test-selector="fastenersTable_item">
                    {productInfo?.productDetailPath ? (
                        <Link
                            {...styles.itemNumberLink}
                            href={productInfo.productDetailPath}
                            data-test-selector="productNumberLink"
                        >
                            {product.productNumber}
                        </Link>
                    ) : (
                        <Typography {...styles.itemNumber}>{product.productNumber}</Typography>
                    )}
                </DataTableCell>
                {showLength && (
                    <DataTableCell {...styles.lengthCell} data-test-selector="fastenersTable_length">
                        {getAttributeTypeValue(constants.productAttributes.length, product.attributeTypes)}
                    </DataTableCell>
                )}
                {showHeadDiameter && (
                    <DataTableCell {...styles.headDiameterCell} data-test-selector="fastenersTable_headDiameter">
                        {getAttributeTypeValue(constants.productAttributes.headDiameter, product.attributeTypes)}
                    </DataTableCell>
                )}
                {showThread && (
                    <DataTableCell {...styles.threadCell} data-test-selector="fastenersTable_thread">
                        {getAttributeTypeValue(constants.productAttributes.thread, product.attributeTypes)}
                    </DataTableCell>
                )}
                {showPoint && (
                    <DataTableCell {...styles.pointCell} data-test-selector="fastenersTable_point">
                        {getAttributeTypeValue(constants.productAttributes.point, product.attributeTypes)}
                    </DataTableCell>
                )}
                {showHead && (
                    <DataTableCell {...styles.headCell} data-test-selector="fastenersTable_head">
                        {getAttributeTypeValue(constants.productAttributes.head, product.attributeTypes)}
                    </DataTableCell>
                )}
                {showFinish && (
                    <DataTableCell {...styles.finishCell} data-test-selector="fastenersTable_finish">
                        {getAttributeTypeValue(constants.productAttributes.finish, product.attributeTypes)}
                    </DataTableCell>
                )}
                <DataTableCell {...styles.actionsCell} data-test-selector="fastenersTable_actions">
                    <StyledWrapper {...styles.actionsWrapper}>
                        <ProductPrice showLabel={false} product={productContext} extendedStyles={styles.productPrice} />
                        <StyledWrapper {...styles.actionsInnerWrapper}>
                            <AddToCartOptionsAndButton
                                extendedStyles={styles.addToCartOptionsAndButton}
                                showUnitOfMeasure={false}
                            />
                            {product.canAddToWishlist && (
                                <ProductAddToListLink
                                    data-test-selector="addToListLink"
                                    extendedStyles={styles.addToListLinkStyles}
                                />
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </DataTableCell>
            </DataTableRow>
        </ProductContext.Provider>
    );
};

export default ProductFastenersDataTableRow;
